import type { Audio as ECAudio } from '../../../dorian-shared/types/audio/audio';
import type {
  AudioStepSettingsType,
  SFXActionSettings,
  MusicActionSettings,
} from '../../../dorian-shared/types/audio/AudioStepSettingsType';

// Path suffix appended to the base bucket URL to build the audio storage prefix.
// Mirrors editor-api src/utils/const.js:6 `const AUDIO_PATH = "audio/custom/"`.
// public_preview.js:723 computes Audio.audioUrl = getAudioBucketUrl() + AUDIO_PATH.
// AudioController.ts:27-29 then appends userId/type/audioName — so audioUrl must end at "audio/custom/".
// Exported so AudioController tests can reference the same constant (DRY).
export const AUDIO_CUSTOM_PATH = 'audio/custom/';

/**
 * Resolves the Audio sub-object for each stepAudio entry using the client-side
 * audios list and the bucket base URL.
 *
 * Extracted from transformNodeToBranchDetails.ts:423-470 so both the frame
 * converter (transformStep) and the audio subscriber (useStepAudioSubscriber)
 * use the same logic — DRY, single source of truth.
 *
 * Invariants (match the original transform block):
 * - Entries without audioId or with action 'none' are filtered out (mina #16).
 * - When audioPath is null: returns filtered entries as-is — audio will be silent,
 *   consistent with pre-fix behaviour on first branch load before audios arrive.
 * - When audioRecord not found: returns entry as-is (defensive, no partial Audio).
 * - When audioRecord.path missing or prefix mismatch: returns entry as-is.
 * - Attaches Audio shape matching the HTTP etalon (public_preview.js:719-724).
 *
 * @param stepAudios - Raw stepAudios from the EC BranchStep (getAllV3, no Audio).
 * @param audios     - Loaded audios list from AudiosProvider context.
 * @param audioPath  - Bucket base URL from deriveAudioBucketUrl (e.g. "https://cdn/").
 *                     Null when audios list is empty or not yet available.
 */
export function resolveStepAudios(
  stepAudios: AudioStepSettingsType | null | undefined,
  audios: ECAudio[],
  audioPath: string | null,
): AudioStepSettingsType | null | undefined {
  if (!Array.isArray(stepAudios)) return stepAudios;

  return stepAudios
    // Filter out action:none / action:stop entries without an audioId — they carry no Audio
    // and HTTP public_preview.js skips them (only StepAudios with a linked Audio row are
    // included in the Sequelize include). This fixes mina #16.
    .filter((sa) => (sa as SFXActionSettings | MusicActionSettings & { audioId?: unknown }).audioId != null
      && sa.action !== 'none')
    .map((sa) => {
      const saWithId = sa as SFXActionSettings | MusicActionSettings & { audioId?: unknown };
      if (!audioPath) {
        // audioPath not yet received (edge case: first branch load before AddBranch mounts).
        // Return as-is — audio will be silent, consistent with the pre-fix behaviour.
        return sa;
      }
      const audioRecord = audios.find(
        (a) => String(a.id) === String((saWithId as { audioId?: unknown }).audioId),
      );
      if (!audioRecord) {
        // Audio not found in the loaded audios (e.g. not shared with book, or still loading).
        // Defensive: return as-is rather than attaching a partial Audio object.
        return sa;
      }
      // Extract audioName from path (format: "audio/custom/{userId}/{type}/{audioName}",
      // audio.js:462). Deterministic prefix replace — NOT split('/').pop() which would
      // be fragile against path variations. AUDIO_CUSTOM_PATH reused (DRY).
      const audioName = audioRecord.path
        ? audioRecord.path.replace(
            `${AUDIO_CUSTOM_PATH}${audioRecord.userId}/${audioRecord.type}/`,
            '',
          )
        : undefined;
      // Defensive: if path absent or prefix didn't match (replace returned full path),
      // return sa as-is — do not attach a broken Audio shape.
      if (!audioName || audioName === audioRecord.path) return sa;
      // Attach Audio shape matching the HTTP etalon (public_preview.js:719-724 Sequelize include).
      // audioUrl = bucket + "audio/custom/" — matches HTTP public_preview.js:723
      // (getAudioBucketUrl() + AUDIO_PATH). AudioController then appends userId/type/audioName.
      // policyId is omitted — absent from EC AudiosProvider type; renderer does not consume it.
      // alias/disabled/createdAt/updatedAt omitted — absent from /v1/audio/book/:bookId SQL
      // select (audio.js:370-391); covered by IGNORE_PATHS skip-list.
      const Audio = {
        audioUrl: audioPath + AUDIO_CUSTOM_PATH,
        userId: audioRecord.userId,
        type: audioRecord.type,
        audioName,
        id: Number(audioRecord.id),
        title: audioRecord.title,
        duration: audioRecord.duration,
      };
      return { ...sa, Audio };
    }) as AudioStepSettingsType;
}
